<template>
  <section class="app-course-context" aria-label="Контекст текущего курса">
    <div class="app-course-context__card">
      <dl class="app-course-context__list">
        <dt class="app-course-context__label">Текущий курс</dt>
        <dd class="app-course-context__value">{{ courseName }}</dd>
        <dt class="app-course-context__label">Локализация</dt>
        <dd class="app-course-context__value">{{ localization }}</dd>
        <template v-if="showActiveModule">
          <dt class="app-course-context__label">Активный модуль</dt>
          <dd class="app-course-context__value">{{ activeModeLabel }}</dd>
        </template>
      </dl>
      <div class="app-course-context__actions">
        <router-link
          v-if="showStepLink"
          :to="stepNavigation.to"
          class="app-course-context__link app-course-context__link--step"
        >
          {{ stepNavigation.label }}
        </router-link>
        <router-link
          v-if="hasCourse"
          :to="courseTarget"
          class="app-course-context__link"
        >
          Сменить курс
        </router-link>
        <router-link
          v-else
          :to="courseTarget"
          class="app-course-context__link app-course-context__link--primary"
        >
          Выбрать курс
        </router-link>
      </div>
    </div>
    <p
      v-if="showAllCoursesWarning"
      class="app-course-context__warning"
      role="status"
    >
      <i class="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
      Показаны расчёты всех курсов
    </p>
  </section>
</template>

<script>
import { useCourseStore, HISTORY_REPORT_SCOPE_ALL } from '@/stores/courseStore';
import { useCalculationStepNavigation } from '@/composables/useCalculationStepNavigation';

export default {
  name: 'AppCourseContextBar',
  setup() {
    const { canShowStepLink, stepNavigation } = useCalculationStepNavigation();
    return { canShowStepLink, stepNavigation };
  },
  computed: {
    courseStore() {
      return useCourseStore();
    },
    isModuleSelectionPage() {
      return this.$route.name === 'calculate';
    },
    hasCourse() {
      return !!this.courseStore.selectedCourseId;
    },
    showActiveModule() {
      return !this.isModuleSelectionPage;
    },
    showStepLink() {
      return this.canShowStepLink && !this.isModuleSelectionPage;
    },
    courseName() {
      if (!this.hasCourse) return 'Курс не выбран';
      const n = (this.courseStore.selectedCourse?.course_name || '').trim();
      return n || '—';
    },
    localization() {
      if (!this.hasCourse) return '—';
      const loc = (this.courseStore.selectedCourse?.localization || '').trim();
      return loc || '—';
    },
    activeModeLabel() {
      return this.courseStore.activeCalculationModuleLabel || 'Не выбран';
    },
    courseTarget() {
      const modeId = this.courseStore.activeCalculationModuleId;
      if (!modeId) return '/app/calculate';
      return { path: '/app/calculate/course', query: { mode: modeId } };
    },
    showAllCoursesWarning() {
      const onHistoryOrReport =
        this.$route.name === 'history' || this.$route.name === 'report';
      return (
        onHistoryOrReport &&
        this.courseStore.historyReportListScope === HISTORY_REPORT_SCOPE_ALL
      );
    },
  },
};
</script>

<style scoped>
.app-course-context {
  margin-bottom: 1rem;
}

.app-course-context__card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem 1.25rem;
  background: var(--tcp-gradient-panel);
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.05);
  padding: 0.75rem 1rem;
}

.app-course-context__list {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 0.6rem;
  row-gap: 0.3rem;
  align-items: baseline;
  margin: 0;
  flex: 1 1 16rem;
  min-width: 0;
}

.app-course-context__label {
  margin: 0;
  color: var(--tcp-color-text-slate);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.app-course-context__label::after {
  content: ':';
}

.app-course-context__value {
  margin: 0;
  color: var(--tcp-color-text-dark);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;
  min-width: 0;
}

.app-course-context__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
  align-self: center;
}

.app-course-context__link--step {
  font-weight: 600;
}

.app-course-context__link {
  font-size: 0.875rem;
  color: var(--tcp-color-brand-start);
  text-decoration: none;
  white-space: nowrap;
}

.app-course-context__link:hover {
  color: var(--tcp-color-brand-hover);
  text-decoration: underline;
}

.app-course-context__link--primary {
  font-weight: 600;
}

.app-course-context__warning {
  margin: 0.5rem 0 0;
  padding: 0.4rem 0.65rem;
  font-size: 0.875rem;
  color: var(--tcp-color-warning-text);
  background: var(--tcp-color-warning-bg);
  border: 1px solid var(--tcp-color-warning-border);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .app-course-context__card {
    padding: 0.65rem 0.85rem;
  }
}
</style>
